import get from 'lodash/get'
import * as React from 'react'

import { stringToBoolean } from '@disco/utils/string-utils'
import { formatDate, formatTime } from '@disco/utils/time-utils'
import { getOptimizedFormat, getOriginalFormat } from '@disco/utils/tracks'

import type { CustomFieldsConfig } from 'types/business'
import type { Track } from 'types/track'

function _omitContactInfo(
  comment: string,
  removeRegexp: RegExp,
): React.ReactNode {
  const parts = comment.split(removeRegexp)
  if (parts.length <= 1) return comment
  const [beginning, capturedSeparatorPart, ...ending] = parts
  // TODO this is a hack to remove the contact info from the comment but needs to be reformatted when rendering comment input in UI to break lines
  return (
    <span>
      <span>{beginning}</span>
      <span data-track-comment-contact-line>{capturedSeparatorPart}</span>
      <span>{ending.join('')}</span>
    </span>
  )
}

export function getTrackField(
  track: Track,
  prop: string,
  customFieldsConfig: CustomFieldsConfig | null | undefined,
  options?: { useMetadataForArtist?: boolean },
): any {
  const { metadata } = track
  const baseMeta = ['Artist', 'Album', 'Name']
  // When called from Overview tab with useMetadataForArtist=true, return metadata.Artist
  // instead of track.artist to show the performing artist instead of the composer
  if (baseMeta.includes(prop)) {
    if (prop === 'Artist' && options?.useMetadataForArtist) {
      return metadata.Artist
    }
    return get(track, prop.toLowerCase())
  }

  // Alias for Name
  if (prop === 'Title') {
    return get(track, 'name')
  }

  if (prop === 'PRO number') {
    return get(metadata, `ProNumber`)
  }

  if (prop === 'Formats') {
    return Object.keys(track.all_files || {})
      .join(', ')
      .toUpperCase()
  }

  if (prop === 'Track order') {
    const { TrackNumber, Tracks } = metadata
    return TrackNumber ? TrackNumber + (Tracks ? ` / ${Tracks}` : '') : ''
  }

  const { created, duration } = track

  if (prop === 'Length') {
    if (!duration) return null
    return formatTime(duration, true)
  }

  if (prop === 'Upload date') {
    if (!created) return null
    return formatDate(created)
  }

  if (prop === 'Uploaded by' || prop === 'Created') {
    const { user } = track
    if (!user) return null
    return `${user.first_name} ${user.last_name}`
  }

  if (prop === 'Comments') {
    const comment = get(metadata, 'Comment', '')
    return _omitContactInfo(
      comment,
      /^((?:Please contact|Contact) .*\n)/, //  `?:` is non-capturing group
    )
  }

  // TODO: remove prop === 'Original name' after migration is complete
  // https://app.asana.com/0/35126173840535/1198906776304407/f
  if (prop === 'Original filename' || prop === 'Original name') {
    const format = getOriginalFormat(track) || getOptimizedFormat(track)

    if (format && track.all_files) {
      return track.all_files[format].original_name
    } else {
      return null
    }
  }

  let value = metadata[prop]

  if (customFieldsConfig) {
    // might not be available on public pages
    const fieldConfig = customFieldsConfig[prop]

    if (fieldConfig) {
      // usually fieldConfig is empty
      switch (fieldConfig.type) {
        case 'boolean':
          return stringToBoolean(value) ? 'Yes' : 'No'

        case 'date':
          return formatDate(value)

        default: // normal case
      }
    }
  }

  return value
}
